import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Tooltip, Link, IconButton, TextField } from "@mui/material";
import { ReactComponent as ContactIcon } from '../../assets/PepWise2.0/contact-phone-black.svg';
import PhoneIcon from "@mui/icons-material/Phone";
import { ReactComponent as InfoIcon } from '../../assets/icon-info.svg';
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ReactComponent as CommingSoonIcon } from '../../assets/coming-soon-dp.svg';
import Loader from "components/Loader/loader";
import { getRunningDays } from "utils/common-util";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CalendarIcon from '../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as RightArrowBoxIcon } from '../../assets/PepWise2.0/Right-Arrow-Icon-Box.svg';
import { ReactComponent as LeftArrowBoxIcon } from '../../assets/PepWise2.0/Left-Arrow-Icon-Box.svg';

const DatePickerIcon = () => (
  <Box
    component="img"
    src={CalendarIcon}
    alt=""
    sx={{
      width: '24px',
      height: '24px',
      display: 'block'
    }}
  />
);


const ProjectTitleBlock = ({ status, navigateToExecutions, colors }: any) => {
  const clickable = !!status?.contactInfo?.programId;
  const hasLink = !!status.contact?.hyperLink;
  const title = status.projectFriendlyName || status.projectName;

  const titleStyle = {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: colors.text.primary
  };

  if (!hasLink) {
    return (
      <Typography
        variant="bodyText"
        sx={{
          ...titleStyle,
          cursor: clickable ? 'pointer' : 'default'
        }}
        onClick={() => clickable && navigateToExecutions(status.contact)}
        title={title}
      >
        {title}
      </Typography>
    );
  }

  return (
    <Link
      href={status.contact?.hyperLink}
      target="_blank"
      rel="noopener"
      color="inherit"
      sx={{
        textDecoration: 'none',
        display: 'block',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography
        variant="bodyText"
        sx={{
          ...titleStyle,
          cursor: 'pointer'
        }}
        title={title}
      >
        {title}
      </Typography>
    </Link>
  );
};

const SlaBlock = ({ status, colors, tooltipProps }: any) => {
  if (!status.isAvailable || (status.isAvailable && status.isAvailable !== 1)) return null;

  return (
    <>
      <Typography
        sx={{
          mt: '9px',
          fontWeight: 500,
          color: colors.text.primary
        }}
      >
        {status.totalJobs ? status.totalJobs : "--"}
      </Typography>

      <Box mt={1}>
        <Typography
          variant="bodyText"
          sx={{
            display: "flex",
            alignItems: "center",
            color: status.colorCode,
            textTransform: 'uppercase',
            fontWeight: 600
          }}
        >
          {status.slaStatus}

          {status.actualStartTime && (
            <Tooltip
              {...tooltipProps}
              title={
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      color: colors.text.secondary,
                      fontWeight: 500,
                      fontStyle: 'italic'
                    }}
                  >
                    Actual Start Time
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '12px',
                      color: colors.text.primary
                    }}
                  >
                    {moment(status.actualStartTime).utc().format("MM-DD-YYYY, hh:mm A")}
                  </Typography>

                  {status.actualCompletionTime && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '13px',
                          mt: '5px',
                          color: colors.text.secondary,
                          fontWeight: 500,
                          fontStyle: 'italic'
                        }}
                      >
                        Actual End Time
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: colors.text.primary
                        }}
                      >
                        {moment(status.actualCompletionTime).utc().format("MM-DD-YYYY, hh:mm A")}
                      </Typography>
                    </>
                  )}
                </>
              }
              arrow
            >
              <Box
                component="span"
                sx={{
                  ml: '5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  '& svg': {
                    width: 18,
                    height: 18,
                    display: 'block'
                  }
                }}
              >
                <InfoIcon />
              </Box>
            </Tooltip>
          )}
        </Typography>
      </Box>
    </>
  );
};

const RunningDaysBlock = ({ status, colors }: any) => {
  if (!status.isAvailable || (status.isAvailable && status.isAvailable !== 1)) return null;

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const runningDays = getRunningDays(status.slaScheduleInfo);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        mt: 1,
        flexWrap: 'wrap'
      }}
    >
      {daysOfWeek.map((day, index) => {
        const isRunningDay = runningDays.has(index);

        return (
          <Box
            key={`day-${day}-${index}`}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: 1,
              backgroundColor: isRunningDay
                ? colors.background.runningDayActive
                : colors.background.card,
              color: isRunningDay
                ? colors.text.inverse
                : colors.border.runningDay,
              border: `1px solid ${isRunningDay ? colors.background.runningDayActive : colors.border.runningDay}`,
              boxSizing: 'border-box',
              flexShrink: 0,
              cursor: 'pointer'
            }}
          >
            {day}
          </Box>
        );
      })}
    </Box>
  );
};

const GreatExpectationsBlock = ({ status, colors }: any) => {
  const value = String(status.dqStatus ?? 'N/A').trim();
  const barColors: Record<string, string> = {
    '1': 'rgba(1, 192, 8, 1)',
    '2': 'rgba(254, 38, 38, 1)',
    '3': 'rgba(253, 120, 59, 1)'
  };
  const barColor = barColors[value];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mt: 1,
        minHeight: 20
      }}
    >
      {barColor ? (
        <Box
          role="img"
          aria-label={`Great Expectations status ${value}`}
          sx={{
            width: '55%',
            height: 20,
            borderRadius: '5px',
            backgroundColor: barColor
          }}
        />
      ) : (
        <Typography
          variant="bodyText"
          sx={{ color: colors.text.secondary }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const ViewHealthButton = ({ status, colors }: any) => {
  if (!status.platformHealthLink) return null;

  return (
    <Typography
      sx={{
        textAlign: 'center',
        margin: '10px 0 8px',
        padding: '2px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        cursor: 'pointer',
        borderRadius: '20px',
        border: `1px solid ${colors.border.muted}`,
        backgroundColor: colors.background.statusMuted,
        color: colors.text.inverse,
        fontWeight: 500,
        lineHeight: 1.2
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(status.platformHealthLink, '_blank', 'noopener,noreferrer');
      }}
    >
      VIEW HEALTH
    </Typography>
  );
};

const PlatformHealthBlock = ({ status, sector, colors, tooltipProps }: any) => {
  const commonTooltip = (
    <>
      <Typography
        variant="body2"
        sx={{
          fontSize: '13px',
          color: colors.text.secondary,
          fontWeight: 500,
          fontStyle: 'italic'
        }}
      >
        Est. Arrival
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: colors.text.primary
        }}
      >
        {status.eta ||
          (["APAC", "FLNA", "PBNA"].includes(sector) ? "Aug 30, 2025" : "--")}
      </Typography>
    </>
  );

  const bottomTooltip = (
    <>
      <Typography
        variant="body2"
        sx={{
          fontSize: '13px',
          color: colors.text.secondary,
          fontWeight: 500,
          fontStyle: 'italic'
        }}
      >
        Est. Arrival
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: colors.text.primary
        }}
      >
        {status?.exeProjectEta ? moment(status?.exeProjectEta).utc().format('MMM DD, YYYY') : '--'}
      </Typography>
    </>
  );

  if ((status.isAvailable || status.isAvailable == 1) && !status.platformHealthLink) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          sx={{
            textAlign: 'center',
            border: `1px solid ${colors.border.muted}`,
            borderRadius: '20px',
            margin: '10px 0 8px',
            padding: '2px 8px',
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '12px',
            backgroundColor: colors.background.statusMuted,
            color: colors.text.inverse,
            fontWeight: 600,
            lineHeight: 1.2
          }}
        >
          COMING SOON
          <Tooltip title={commonTooltip} arrow {...tooltipProps}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.5,
                cursor: 'pointer',
                lineHeight: 0,
                '& svg': {
                  width: 14,
                  height: 14,
                  display: 'block'
                },
                '& svg path': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg circle': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg rect': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
              }}
            >
              <CommingSoonIcon />
            </Box>
          </Tooltip>
        </Typography>
      </Box>
    );
  }

  if (!status.isAvailable) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            border: `1px solid ${colors.border.muted}`,
            borderRadius: '20px',
            padding: '2px 8px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            backgroundColor: colors.background.statusMuted,
            color: colors.text.inverse,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap'
          }}
        >
          COMING SOON
          <Tooltip title={bottomTooltip} arrow {...tooltipProps}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.5,
                cursor: 'pointer',
                lineHeight: 0,
                '& svg': {
                  width: 14,
                  height: 14,
                  display: 'block'
                },
                '& svg path': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg circle': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
                '& svg rect': {
                  fill: '#ffffff !important',
                  stroke: '#ffffff !important',
                },
              }}
            >
              <CommingSoonIcon />
            </Box>
          </Tooltip>
        </Typography>
      </Box>
    );
  }

  return null;
};

const ProgramDataCard = ({ programId, projectDetails, dataCardLoader, handleDateChange, sector }: any) => {

  const theme = useTheme();
  const colors = theme.palette.dataPipeline.accordionWidget;

  const tooltipProps = {
    componentsProps: {
      tooltip: {
        sx: {
          backgroundColor: `${colors.background.tooltip} !important`,
          color: colors.text.primary
        }
      },
      arrow: {
        sx: {
          color: `${colors.background.tooltip} !important`
        }
      }
    }
  };

  const minDate = new Date('2025-03-18');
  const pageSize = 4;

  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalPages =
    projectDetails.length > pageSize
      ? Math.ceil(projectDetails.length / pageSize)
      : 1;

  const currentHops = projectDetails.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const loader = false;
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentPage(1);
  }, [projectDetails]);

  const navigateToExecutions = (navigateData: any) => {
    let date = moment(projectDetails.filter((pj: any) => pj.sectors == 'APAC' ? pj.order == 2 : pj.projectName)[0]?.scheduleDate.split('T')[0]).format("YYYY-MM-DD")
    if (navigateData.hyperLink) {
      navigate(navigateData.hyperLink)
    } else if (navigateData.sstnPrjctId && navigateData.sstnPrjctId !== '-1') {
      navigate(`/data-pipeline/projects/${navigateData.programId}/${navigateData.sstnPrjctId}/${encodeURIComponent(navigateData.programName)}/${encodeURIComponent(navigateData.projectName)}?startingDate=${date}&endingDate=${date}&dateFilter=Today&isSector=true`)
    } else {
      navigate(`/data-pipeline/programs/${navigateData.programId}/${encodeURIComponent(navigateData.programName)}`)
    }

  }

  const handleDateSelect = (stDate: any) => {
    const sd = moment(stDate).startOf('day').format('YYYY-MM-DD');

    handleDateChange({
      scheduleDate: sd,
      programId
    })
  }

  const handleNext = () => {

    if (currentPage < totalPages) {

      setCurrentPage((prev: number) => prev + 1)

    }

  }

  const handlePrev = () => {

    if (currentPage > 1) {

      setCurrentPage((prev: number) => prev - 1)

    }

  }

  const handleGoToPage = (page: number) => {

    if (page < 1 || page > totalPages) return;

    setCurrentPage(page)

  }

  return (
    <Grid
      container
      sx={{
        backgroundColor: colors.background.page
      }}
    >
      {loader ? <Loader /> :
        <>
          <Grid
            container
            p={2}
            pb={0}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexWrap: 'wrap',
              rowGap: 1.5
            }}
          >
            <Grid item xs={12} md="auto">

              <LocalizationProvider
                className="outerDateContainer"
                dateAdapter={AdapterMoment}
              >
                <DatePicker
                  showToolbar={false}
                  components={{
                    OpenPickerIcon: DatePickerIcon,
                    LeftArrowIcon: RightArrowBoxIcon,
                    RightArrowIcon: LeftArrowBoxIcon,
                  }}

                  minDate={minDate}
                  disableFuture={true}

                  className="dateFilterDataPipeline"

                  value={moment(projectDetails.find((pj: any) => pj.sectors == 'APAC' ? pj.projectName == 'Gold' : pj.projectName)?.scheduleDate?.split('T')[0])}

                  onChange={(dateTime) => {
                    handleDateSelect(dateTime);
                  }}

                  PopperProps={{
                    sx: {
                      '& .MuiPaper-root': {
                        backgroundColor: `${theme.palette.dateFilter.popoverBackground} !important`,
                        backgroundImage: 'none !important',
                        color: `${theme.palette.dateFilter.popoverText} !important`,
                        borderRadius: '8px',
                        boxShadow: theme.palette.dateFilter.popoverShadow,
                        overflow: 'hidden',
                      },

                      '& .MuiCalendarPicker-root': {
                        backgroundColor: `${theme.palette.dateFilter.calendarBackground} !important`,
                        color: `${theme.palette.dateFilter.dayText} !important`,
                      },

                      '& .MuiCalendarPicker-viewTransitionContainer': {
                        backgroundColor: `${theme.palette.dateFilter.calendarBackground} !important`,
                      },

                      '& .MuiPickersCalendarHeader-root': {
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                      },

                      '& .MuiPickersCalendarHeader-label': {
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                        fontWeight: 600,
                        fontSize: '14px',
                      },

                      '& .MuiPickersCalendarHeader-switchViewButton': {
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                      },

                      '& .MuiPickersCalendarHeader-switchViewIcon': {
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                      },

                      '& .MuiPickersArrowSwitcher-button': {
                        padding: '4px',
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                      },

                      '& .MuiPickersArrowSwitcher-button.Mui-disabled': {
                        color: `${theme.palette.dateFilter.calendarHeaderText} !important`,
                        opacity: 0.35,
                      },

                      '& .MuiDayPicker-weekDayLabel': {
                        color: `${theme.palette.dateFilter.weekDayText} !important`,
                        fontSize: '12px',
                      },

                      '& .MuiPickersDay-root': {
                        color: `${theme.palette.dateFilter.dayText} !important`,
                        fontSize: '14px',

                        '&:hover': {
                          backgroundColor: `${theme.palette.dateFilter.dayHoverBackground} !important`,
                          color: `${theme.palette.dateFilter.dayHoverText} !important`,
                        },
                      },

                      '& .MuiPickersDay-root.Mui-selected': {
                        backgroundColor: `${theme.palette.dateFilter.rangeBackground} !important`,
                        color: `${theme.palette.dateFilter.dayTextInRange} !important`,

                        '&:hover': {
                          backgroundColor: `${theme.palette.dateFilter.rangeBackground} !important`,
                          color: `${theme.palette.dateFilter.dayTextInRange} !important`,
                        },
                      },

                      '& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth': {
                        color: `${theme.palette.dateFilter.dayText} !important`,
                        backgroundColor: 'transparent !important',
                        opacity: 0.65,
                      },

                      '& .MuiPickersDay-today': {
                        borderColor: `${theme.palette.dateFilter.rangeBackground} !important`,
                        color: `${theme.palette.dateFilter.dayText} !important`,
                      },

                      '& .MuiPickersDay-root.Mui-disabled, & .MuiPickersDay-root.MuiButtonBase-root.Mui-disabled': {
                        color: `${theme.palette.dateFilter.dayText} !important`,
                        WebkitTextFillColor: `${theme.palette.dateFilter.dayText} !important`,
                        opacity: '0.55 !important',
                        visibility: 'visible !important',
                        backgroundColor: 'transparent !important',
                      },

                      '& .MuiPickersDay-root.Mui-disabled:hover, & .MuiPickersDay-root.MuiButtonBase-root.Mui-disabled:hover': {
                        backgroundColor: 'transparent !important',
                        color: `${theme.palette.dateFilter.dayText} !important`,
                        WebkitTextFillColor: `${theme.palette.dateFilter.dayText} !important`,
                      },

                      '& .MuiYearPicker-root': {
                        backgroundColor: `${theme.palette.dateFilter.calendarBackground} !important`,
                      },

                      '& .PrivatePickersYear-yearButton, & .MuiPickersYear-yearButton': {
                        color: `${theme.palette.dateFilter.dayText} !important`,

                        '&:hover': {
                          backgroundColor: `${theme.palette.dateFilter.dayHoverBackground} !important`,
                          color: `${theme.palette.dateFilter.dayHoverText} !important`,
                        },
                      },

                      '& .PrivatePickersYear-yearButton.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected': {
                        backgroundColor: `${theme.palette.dateFilter.rangeBackground} !important`,
                        color: `${theme.palette.dateFilter.dayTextInRange} !important`,
                      },

                      '& .PrivatePickersYear-yearButton.Mui-disabled, & .MuiPickersYear-yearButton.Mui-disabled': {
                        color: `${theme.palette.dateFilter.dayText} !important`,
                        opacity: 0.35,
                      },

                      '& .MuiMonthPicker-root': {
                        backgroundColor: `${theme.palette.dateFilter.calendarBackground} !important`,
                      },

                      '& .PrivatePickersMonth-root, & .MuiPickersMonth-monthButton': {
                        color: `${theme.palette.dateFilter.dayText} !important`,

                        '&:hover': {
                          backgroundColor: `${theme.palette.dateFilter.dayHoverBackground} !important`,
                          color: `${theme.palette.dateFilter.dayHoverText} !important`,
                        },
                      },

                      '& .PrivatePickersMonth-root.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected': {
                        backgroundColor: `${theme.palette.dateFilter.rangeBackground} !important`,
                        color: `${theme.palette.dateFilter.dayTextInRange} !important`,
                      },
                    },
                  }}

                  renderInput={(params: any) => (
                    <TextField
                      name="scheduleDateTime"
                      {...params}
                      sx={{
                        backgroundColor: `${theme.palette.dateFilter.triggerHoverBackground} !important`,
                        cursor: 'pointer',

                        '& .MuiInputBase-root': {
                          padding: '0 8px',
                          backgroundColor: `${theme.palette.dateFilter.triggerHoverBackground} !important`,
                          color: theme.palette.dateFilter.triggerText,

                          '&:hover': {
                            backgroundColor: `${theme.palette.dateFilter.triggerHoverBackground} !important`,
                          },
                        },

                        '& .MuiOutlinedInput-root': {
                          backgroundColor: `${theme.palette.dateFilter.triggerHoverBackground} !important`,
                        },

                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },

                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },

                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },

                        '& .MuiInputBase-input': {
                          padding: '8px 4px',
                          fontSize: '14px',
                          color: `${theme.palette.dateFilter.triggerText} !important`,
                          WebkitTextFillColor: `${theme.palette.dateFilter.triggerText} !important`,
                          width: '90px',
                        },

                        '& .Mui-focused .MuiInputBase-input': {
                          color: `${theme.palette.dateFilter.triggerTextActive} !important`,
                          WebkitTextFillColor: `${theme.palette.dateFilter.triggerTextActive} !important`,
                        },

                        '& .MuiIconButton-root': {
                          padding: '4px',
                          backgroundColor: 'transparent !important',
                        },

                        '& .MuiInputAdornment-root': {
                          marginLeft: '4px',
                          marginRight: '8px',
                        },

                        '& fieldset': {
                          border: 'none',
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

            </Grid>

            {
              projectDetails.length > 0 &&
              <Grid
                item
                xs={12}
                md="auto"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: { xs: 'flex-end', md: 'center' },
                  gap: 1,
                  flexWrap: 'wrap',
                  flexDirection: { xs: 'column', md: 'row' }
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 300,
                    lineHeight: 1,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    width: { xs: '100%', md: 'auto' },
                    textAlign: { xs: 'right', md: 'left' }
                  }}
                >
                  {`Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(
                    currentPage * pageSize,
                    projectDetails.length
                  )} of ${projectDetails.length} hops`}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'flex-end', md: 'flex-start' }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => currentPage > 1 && handleGoToPage(1)}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === 1
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === 1 ? 'default' : 'pointer'
                    }}
                  >
                    <FirstPageIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => currentPage > 1 && handlePrev()}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === 1
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === 1 ? 'default' : 'pointer'
                    }}
                  >
                    <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap'
                    }}
                  >
                    {Array.from({ length: totalPages }, (_, index) => {
                      const page = index + 1;
                      const isActive = currentPage === page;

                      return (
                        <Box
                          key={page}
                          onClick={() => handleGoToPage(page)}
                          sx={{
                            width: '20px',
                            height: '20px',
                            p: 0.3,
                            borderRadius: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: 1,
                            color: isActive
                              ? colors.pagination.pageActiveText
                              : colors.pagination.pageDefaultText,
                            backgroundColor: isActive
                              ? colors.pagination.pageActiveBackground
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: isActive
                                ? colors.pagination.pageActiveBackground
                                : colors.pagination.pageHoverBackground
                            }
                          }}
                        >
                          {page}
                        </Box>
                      );
                    })}
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => currentPage < totalPages && handleNext()}
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === totalPages
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === totalPages ? 'default' : 'pointer'
                    }}
                  >
                    <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() =>
                      currentPage < totalPages &&
                      handleGoToPage(totalPages)
                    }
                    sx={{
                      p: 0.25,
                      color:
                        currentPage === totalPages
                          ? colors.pagination.iconDisabled
                          : colors.pagination.iconActive,
                      cursor: currentPage === totalPages ? 'default' : 'pointer'
                    }}
                  >
                    <LastPageIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Grid>
            }
          </Grid>

          <Box
            sx={{
              width: '100%',
              backgroundColor: colors.background.page
            }}
          >
            {
              dataCardLoader ? <Loader /> :
              <Grid
                container
                p={2}
                spacing={2}
              >
                <Grid item xs={12} md={2}>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{
                      color: colors.text.primary,
                      lineHeight: '1.5',
                      mt: { xs: 0, md: 7 },
                      mb: 1.5
                    }}
                  >
                    Total Pipelines :
                  </Typography>

                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{
                      color: colors.text.primary,
                      pb: .7,
                      mb: .6
                    }}
                  >
                    SLA Status :
                  </Typography>

                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{
                      color: colors.text.primary,
                      pb: .7,
                      mb: .6
                    }}
                  >
                    Platform Health :
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <Typography
                      variant="bodyText"
                      sx={{
                        color: colors.text.primary
                      }}
                    >
                      Running Days
                    </Typography>

                    <InfoTooltip
                      label="Running Days are displayed based on SLA configuration"
                      tooltipProps={{
                        placement: 'top'
                      }}
                    />

                    <Typography
                      variant="bodyText"
                      sx={{
                        color: colors.text.primary
                      }}
                    >
                      :
                    </Typography>

                    <CalendarMonthIcon
                      sx={{
                        fontSize: '18px',
                        color: colors.text.primary
                      }}
                    />
                  </Box>
                  <Typography
                    variant="bodyText"
                    display="block"
                    sx={{
                      color: colors.text.primary,
                      mt: '18px',
                      pb: .7,
                      mb: .6
                    }}
                  >
                    Great Expectations :
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={10}
                  sx={{
                    pl: { xs: 1.5, sm: 1.5, md: 0 }
                  }}
                >
                  <Grid
                    container
                    columnSpacing={{ xs: 2, sm: 3, md: 3 }}
                    rowSpacing={2}
                  >
                    {currentHops.map((status: any) => {
                      const hasTitle = Boolean(status.projectFriendlyName || status.projectName);

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          key={`${status.projectFriendlyName || ""}-${status.projectName || ""}`}
                          sx={{
                            display: 'flex'
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              minHeight: '175px',
                              opacity: status.isAvailable ? 1 : '.5',
                              backgroundColor: colors.background.card,
                              color: colors.text.primary,
                              position: 'relative',
                              pl: { xs: '30px', sm: '28px', md: '24px' },
                              pr: { xs: '28px', sm: '30px', md: '32px' },
                              pt: '14px',
                              pb: '14px',
                              boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
                              clipPath: {
                                xs: 'polygon(0 0, 95% 0, 100% 50%, 95% 100%, 0 100%, 2% 50%)',
                                sm: 'polygon(0 0, 95% 0, 100% 50%, 95% 100%, 0 100%, 2.5% 50%)',
                                md: 'polygon(0 0, 95% 0, 100% 50%, 95% 100%, 0 100%, 3% 50%)'
                              },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              overflow: 'hidden'
                            }}
                          >
                            {hasTitle && (
                              <Box
                                mb={1}
                                sx={{
                                  width: '100%',
                                  minWidth: 0
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pb: 0.5,
                                    borderBottom: `1px solid ${colors.border.subtle}`
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <ProjectTitleBlock
                                      status={status}
                                      navigateToExecutions={navigateToExecutions}
                                      colors={colors}
                                    />
                                  </Box>

                                  {status?.contactInfo?.name && (
                                    <Tooltip
                                      arrow
                                      {...tooltipProps}
                                      title={
                                        <>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '13px',
                                              color: colors.text.secondary,
                                              fontWeight: 500,
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            Contact Person
                                          </Typography>

                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '12px',
                                              color: colors.text.primary
                                            }}
                                          >
                                            {status?.contactInfo?.name || 'Godwin'}
                                          </Typography>

                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '12px',
                                              fontWeight: 500,
                                              color: colors.text.primary,
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            {status?.contactInfo?.email}
                                          </Typography>

                                          {status?.contactInfo?.phone &&
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: '12px',
                                                color: colors.text.primary
                                              }}
                                            >
                                              <PhoneIcon
                                                sx={{
                                                  verticalAlign: "middle",
                                                  width: '15px',
                                                  height: '15px',
                                                  color: 'blue'
                                                }}
                                              /> {status?.contactInfo?.phone}
                                            </Typography>
                                          }
                                        </>
                                      }
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                          width: '24px',
                                          height: '24px',
                                          '& svg': {
                                            display: 'block',
                                            maxWidth: '22px',
                                            maxHeight: '22px'
                                          }
                                        }}
                                      >
                                        <ContactIcon />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            )}

                            {status.contact?.hyperLink ? (
                              <Link
                                href={status.contact.hyperLink}
                                sx={{
                                  textDecoration: 'none',
                                  padding: '0px',
                                  color: 'inherit',
                                  cursor: status?.contactInfo?.programId ? 'pointer' : 'default'
                                }}
                                target="_blank"
                                rel="noopener"
                                color='inherit'
                              >
                                <SlaBlock
                                  status={status}
                                  colors={colors}
                                  tooltipProps={tooltipProps}
                                />

                                <PlatformHealthBlock
                                  status={status}
                                  sector={sector}
                                  colors={colors}
                                  tooltipProps={tooltipProps}
                                />

                                <ViewHealthButton
                                  status={status}
                                  colors={colors}
                                />

                                <RunningDaysBlock
                                  status={status}
                                  colors={colors}
                                />

                                <GreatExpectationsBlock
                                  status={status}
                                  colors={colors}
                                />
                              </Link>
                            ) : (
                              <Box
                                onClick={() => status?.contactInfo?.programId && navigateToExecutions(status.contact)}
                              >
                                <SlaBlock
                                  status={status}
                                  colors={colors}
                                  tooltipProps={tooltipProps}
                                />

                                <PlatformHealthBlock
                                  status={status}
                                  sector={sector}
                                  colors={colors}
                                  tooltipProps={tooltipProps}
                                />

                                <ViewHealthButton
                                  status={status}
                                  colors={colors}
                                />

                                <RunningDaysBlock
                                  status={status}
                                  colors={colors}
                                />

                                <GreatExpectationsBlock
                                  status={status}
                                  colors={colors}
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            }

            <Grid
              container
              p={2}
              pb={0}
            >
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    borderTop: `1px solid ${colors.border.expandedAccordion}`,
                    pt: '15px'
                  }}
                />
              </Grid>
            </Grid>

            <Grid
              container
              p={2}
              spacing={2}
            >
              <Grid item xs={12} md={2}>
                <Typography
                  variant="widgetTitle"
                  sx={{
                    color: colors.text.secondary
                  }}
                >
                  Historic Trend
                </Typography>

                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{
                    color: colors.text.primary,
                    mt: 1.8,
                    mb: .6
                  }}
                >
                  SLA Type :
                </Typography>

                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{
                    color: colors.text.primary,
                    mb: .6
                  }}
                >
                  Last 7 days :
                </Typography>

                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{
                    color: colors.text.primary,
                    mb: .6
                  }}
                >
                  Last 14 days :
                </Typography>

                <Typography
                  variant="bodyText"
                  display="block"
                  sx={{
                    color: colors.text.primary
                  }}
                >
                  Last 30 days :
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={10}
                sx={{
                  pl: { xs: 1.5, sm: 1.5, md: 0 }
                }}
              >
                <Grid container spacing={2}>
                  {currentHops.map((status: any, index: number) => {
                    const hasTitle = Boolean(status.projectFriendlyName || status.projectName);

                    return (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        key={`${status.projectName}-${index}`}
                        sx={{
                          display: 'flex'
                        }}
                      >
                        <Grid
                          container
                          sx={{
                            opacity: !status.isAvailable ? '.5' : 1,
                            backgroundColor: colors.background.card,
                            color: colors.text.primary,
                            position: 'relative',
                            px: '14px',
                            py: '12px',
                            width: '100%',
                            borderRadius: '4px',
                            minHeight: '138px',
                            boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)'
                          }}
                        >
                          {hasTitle && (
                            <Box
                              mb={1}
                              sx={{
                                display: 'inline-block',
                                width: '100%',
                                position: 'relative'
                              }}
                            >
                              <Typography
                                variant="bodyText"
                                display="block"
                                sx={{
                                  borderBottom: `1px solid ${colors.border.subtle}`,
                                  ml: '10px',
                                  pl: '5px',
                                  width: '90%',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  color: colors.text.primary
                                }}
                              >
                                {status.projectFriendlyName || status.projectName}
                              </Typography>
                            </Box>
                          )}

                          <Grid container>
                            <Grid item xs={6}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  width: '100%'
                                }}
                              >
                                <Typography
                                  variant="bodyText"
                                  display="block"
                                  sx={{
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    color: colors.text.secondary
                                  }}
                                >
                                  SLA Met
                                </Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={6}>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  width: '100%'
                                }}
                              >
                                <Typography
                                  variant="bodyText"
                                  display="block"
                                  sx={{
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    color: colors.text.secondary
                                  }}
                                >
                                  SLA Missed
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {(status.isAvailable || status.isAvailable == 1) && <>
                            <Grid container>
                              {status.isAvailable && status.slaTrend.last7days.map((count: number, index: number) => {
                                return (
                                  <Grid item xs={6} key={`7d-${status.projectFriendlyName}-${index}`}>
                                    <Box
                                      sx={{
                                        display: 'inline-block',
                                        width: '100%'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          textAlign: 'center',
                                          fontWeight: 500,
                                          color: colors.text.primary
                                        }}
                                      >
                                        {count || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )
                              })}
                            </Grid>

                            <Grid container>
                              {status.isAvailable && status.slaTrend.last30days.map((count: number, index: number) => {
                                return (
                                  <Grid item xs={6} key={`30d-${status.projectFriendlyName}-${index}`}>
                                    <Box
                                      sx={{
                                        display: 'inline-block',
                                        width: '100%'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          textAlign: 'center',
                                          fontWeight: 500,
                                          color: colors.text.primary
                                        }}
                                      >
                                        {count || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )
                              })}
                            </Grid>

                            <Grid container>
                              {status.isAvailable && status.slaTrend.last90days.map((count: number, index: number) => {
                                return (
                                  <Grid item xs={6} key={`90d-${status.projectFriendlyName}-${index}`}>
                                    <Box
                                      sx={{
                                        display: 'inline-block',
                                        width: '100%'
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          textAlign: 'center',
                                          fontWeight: 500,
                                          color: colors.text.primary
                                        }}
                                      >
                                        {count || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )
                              })}
                            </Grid>
                          </>}

                          {!status.isAvailable &&
                            <Grid
                              container
                              sx={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      textAlign: 'center',
                                      padding: '2px 0',
                                      width: '130px',
                                      fontSize: '12px',
                                      color: colors.text.primary
                                    }}
                                  >
                                    COMING SOON{" "}
                                    <Tooltip
                                      {...tooltipProps}
                                      title={
                                        <>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '13px',
                                              color: colors.text.secondary,
                                              fontWeight: 500,
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            Est. Arrival
                                          </Typography>

                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontSize: '12px',
                                              color: colors.text.primary
                                            }}
                                          >
                                            {status?.exeProjectEta ? moment(status?.exeProjectEta).utc().format('MMM DD, YYYY') : '--'}
                                          </Typography>
                                        </>
                                      }
                                      arrow
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-flex',
                                          verticalAlign: 'text-bottom'
                                        }}
                                      >
                                        <CommingSoonIcon />
                                      </Box>
                                    </Tooltip>
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          }

                        </Grid>
                      </Grid>
                    )
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </>
      }
    </Grid>
  );
};

export default ProgramDataCard;